import AWS from "aws-sdk";
import { useState, useEffect } from "react";
import { useUser } from "../utils/auth";
import { useAppliedPrograms } from "./course-cards";
import { toast } from "react-toastify";

const ApplicationForm = ({ isOpen, onClose, programId, programName, universityName, onApplicationSubmit }) => {
  const { user, isAuthenticated } = useUser();
  const { setAppliedStatus } = useAppliedPrograms();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    tenthMarks: null,
    twelfthMarks: null,
    graduationMarks: null,
    graduationDegree: null
  });

  // Pre-fill user data when component mounts or user changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phoneNumber || ''
      }));
    }
  }, [user]);

  if (!isOpen) return null;
  
  // Prevent non-authenticated users from applying
  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Login Required</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="mb-4">Please log in to submit an application.</p>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  const spacesEndpoint = new AWS.Endpoint("blr1.digitaloceanspaces.com");
  const s3 = new AWS.S3({
    endpoint: spacesEndpoint,
    accessKeyId: "DO00LJDADLLWW324HD96",
    secretAccessKey: "an9TvdcL2ziIfy7jwMQNb9Rr22LNrvRgx5AdYVKviJc",
  });

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files[0]) {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
  
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/upload`, {
        method: 'POST',
        body: formData
      });
  
      if (!response.ok) {
        throw new Error('Upload failed');
      }
  
      const result = await response.json();
      return result.filePath;
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Show success toast and close dialog immediately (optimistic UI update)
      toast.success("Your application has been submitted successfully!");
      
      // Update the context state immediately (optimistic update)
      if (user?.id) {
        setAppliedStatus(programId, true);
      }
      
      // Close dialog immediately
      onClose();

      // Continue with the actual submission in the background
      try {
        // Upload files and get URLs
        const tenthMarksUrl = await uploadFile(formData.tenthMarks, `tenth_${Date.now()}`);
        const twelfthMarksUrl = await uploadFile(formData.twelfthMarks, `twelfth_${Date.now()}`);
        const graduationMarksUrl = formData.graduationMarks
          ? await uploadFile(formData.graduationMarks, `grad_marks_${Date.now()}`)
          : null;
        const graduationDegreeUrl = formData.graduationDegree
          ? await uploadFile(formData.graduationDegree, `grad_degree_${Date.now()}`)
          : null;

        const applicationData = {
          name: formData.name || "N/A",
          email: formData.email || "N/A",
          phone: formData.phone || "N/A",
          tenthMarks: tenthMarksUrl || "Not provided",
          twelfthMarks: twelfthMarksUrl || "Not provided",
          graduationMarks: graduationMarksUrl || "Not provided",
          graduationDegree: graduationDegreeUrl || "Not provided",
          programId: programId,
          programName: programName,
          universityName: universityName,
          appliedDate: new Date().toISOString()
        };

        console.log(applicationData);

        // First, save program as applied in user profile
        if (user?.id) {
          const token = localStorage.getItem('token');
          const applyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/apply-program`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              userId: user.id,
              programId: programId,
              programName: programName,
              universityName: universityName,
              applicationData: applicationData
            }),
          });
          
          if (!applyResponse.ok) {
            throw new Error("Failed to save program application to user profile");
          }
        }

        // Then send application data to backend
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-application`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(applicationData),
        });

        if (!response.ok) {
          throw new Error("Failed to send application data");
        }
        
        // Call the onApplicationSubmit callback only after successful API calls
        if (onApplicationSubmit) {
          onApplicationSubmit();
        }
      } catch (error) {
        // If anything fails in the background, revert the optimistic updates
        console.error("Error in background submission:", error);
        
        // Revert the applied status in context
        if (user?.id) {
          setAppliedStatus(programId, false);
        }
        
        // Show error toast
        toast.error("Application submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Error preparing submission:", error);
      toast.error("There was an error preparing your application. Please try again.");
    }
  };


return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
       
      <div className="bg-white rounded-lg w-full max-w-lg 2xl:max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Apply Now</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  required
                  disabled
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 focus:outline-none cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">Profile data - cannot be changed</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  required
                  disabled
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 focus:outline-none cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">Profile data - cannot be changed</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  required
                  disabled
                  pattern="[0-9]{10}"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 focus:outline-none cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">Profile data - cannot be changed</p>
              </div>
            </div>

            {/* Required Documents */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Required Documents(max. size 12kb)</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  10th Marksheet(Image/Pdf) <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  name="tenthMarks"
                  required
                  accept="image/*,.pdf"
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  onChange={handleFileChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  12th Marksheet(Image/Pdf) <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  name="twelfthMarks"
                  required
                  accept="image/*,.pdf"
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  onChange={handleFileChange}
                />
              </div>
            </div>

            {/* Optional Documents */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Optional Documents</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                Graduation Marksheet(Image/Pdf)
                </label>
                <input
                  type="file"
                  name="graduationMarks"
                  accept="image/*,.pdf"
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  onChange={handleFileChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  College Degree(Image/Pdf)
                </label>
                <input
                  type="file"
                  name="graduationDegree"
                  accept="image/*,.pdf"
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  onChange={handleFileChange}
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Submit Application
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;